
import { Injectable, NgZone } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { API_PATHS } from '../constants/api-paths';
import { BaseService } from './base-service';
import { CacheService } from './cache-service';
import { CACHE_KEYS } from '../constants/cache-keys';
import { SRP_ROUTES } from '../constants/routes';
import { LogoutService } from './logout-service';
import { ConfirmationDialog } from '../confirmation-dialog/confirmation-dialog';
import { ContentFilterPipe } from '../pipes/content-filter-pipe';
import { COMPONENT_META_DATA } from '../constants/component-meta-data';
import { Label } from '../models/page-components';
import { ReusableService } from './reusable-services';

const DEFAULT_INACTIVITY_WARNING_MS = 30 * 60 * 1000; // used when appConfig sessionTimeout is missing or invalid

const DEFAULT_SESSION_TIMEOUT_LABELS = {
  heading: 'Session Expiration Warning!',
  description: 'This session is about to expire in.',
  supportingText: 'You will be automatically signed out.',
  supportingText2: 'Please click on "stay signed in" to continue your session.',
  signOut: 'SIGN OUT',
  staySignedIn: 'STAY SIGNED IN'
};

@Injectable({
  providedIn: 'root',
})
export class GigyaSessionService {
  private readonly activityEvents = ['mousemove', 'keydown', 'click', 'touchstart'];
  private readonly checkIntervalMs = 60 * 1000; // run periodic checks every 1 minute
  private readonly activeWindowMs = 60 * 1000; // user is considered active if interacted within last 1 minute
  private readonly autoRenewThresholdMs = 15 * 60 * 1000; // Auto-renew when <= 15 minutes remain for active users
  private readonly warningThresholdMs = 4 * 60 * 1000; // Show warning when <= 4 minutes remain
  private sessionStartedAt = Date.now();
  private readonly sessionMaxMs = 10 * 60 * 60 * 1000; // in ms (10 hours)

  private intervalId?: ReturnType<typeof setInterval>;
  private lastActivityAt = Date.now();
  private readonly onActivity = () => {
    this.lastActivityAt = Date.now();
  };

  private inactivityWarningThresholdMs = DEFAULT_INACTIVITY_WARNING_MS; // set from appConfig on startMonitoring
  private promptInProgress = false;
  private warningCountdownTimeoutId?: ReturnType<typeof setTimeout>;
  private sessionTimeoutDialogRef: MatDialogRef<ConfirmationDialog> | null = null;
  private sessionTimeoutLabels = DEFAULT_SESSION_TIMEOUT_LABELS;
  private readonly contentPipe = new ContentFilterPipe();
  private appContext = '';

  /**
   * Injects the Angular, app, and Gigya collaborators used for session monitoring, dialogs, renewal, and logout.
   */
  constructor(
    private readonly zone: NgZone,
    private readonly dialog: MatDialog,
    private readonly baseService: BaseService,
    private readonly cacheService: CacheService,
    private readonly logoutService: LogoutService,
    private readonly reusableService: ReusableService
  ) { }

  /**
   * Builds the session timeout dialog labels from CMS page content.
   * Any label missing from the content falls back to its default text.
   *
   * @param pageContent - Label entries loaded for the page; may be empty or undefined.
   */
  setPageContent(pageContent: Label[]): void {
    const { LABELS } = COMPONENT_META_DATA.SESSION_TIMEOUT;
    const content = pageContent || [];
    this.sessionTimeoutLabels = {
      heading: this.contentPipe.transform(content, LABELS.HEADING) || DEFAULT_SESSION_TIMEOUT_LABELS.heading,
      description: this.contentPipe.transform(content, LABELS.DESCRIPTION) || DEFAULT_SESSION_TIMEOUT_LABELS.description,
      supportingText: this.contentPipe.transform(content, LABELS.SUPPORTING_TEXT) || DEFAULT_SESSION_TIMEOUT_LABELS.supportingText,
      supportingText2: this.contentPipe.transform(content, LABELS.SUPPORTING_TEXT_2) || DEFAULT_SESSION_TIMEOUT_LABELS.supportingText2,
      signOut: this.contentPipe.transform(content, LABELS.SIGN_OUT) || DEFAULT_SESSION_TIMEOUT_LABELS.signOut,
      staySignedIn: this.contentPipe.transform(content, LABELS.STAY_SIGNED_IN) || DEFAULT_SESSION_TIMEOUT_LABELS.staySignedIn
    };
  }

  /**
   * Starts session monitoring after login.
   * Reads the inactivity limit from the cached app config (in minutes, default 30), resets the session and
   * activity timers, registers activity listeners, and schedules a check every minute outside Angular's zone.
   * Calling it again restarts the interval.
   *
   * @param appContext - App context used when navigating to the login route after logout.
   */
  startMonitoring(appContext: string) {
    this.appContext = appContext;

    // config_value from appConfig (sessionTimeout) is in minutes
    const configMinutes = parseInt(this.cacheService.getFromCache(CACHE_KEYS.APPCONFIG.SESSION_TIMEOUT), 10);
    console.log('Config inactivity timeout (minutes):', configMinutes);
    this.inactivityWarningThresholdMs = !isNaN(configMinutes) ? configMinutes * 60 * 1000 : DEFAULT_INACTIVITY_WARNING_MS;


    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    this.sessionStartedAt = Date.now();
    this.lastActivityAt = Date.now();

    // reset on activity
    this.activityEvents.forEach((ev) => window.addEventListener(ev, this.onActivity, { passive: true }));

    // periodic check outside Angular for perf
    this.zone.runOutsideAngular(() => {
      this.intervalId = setInterval(() => {
        void this.checkAndRenew();
      }, this.checkIntervalMs);
    });

    // Run once immediately so timing logic is active from startup.
    void this.checkAndRenew();
  }


  /**
   * Periodic check run by the monitoring interval.
   * Runs {@link sessionExtension} only when the cached session type is `gigya` or `sso`.
   * Gigya session validation itself is handled by the Web SDK (`verifyLoginInterval`).
   */
  async checkAndRenew() {
    // Drive local session UX off the app timer and let Gigya Web SDK perform
    // its own background session validation via verifyLoginInterval.
    const sessionType = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.SESSION_TYPE);

    if (sessionType === 'gigya' || sessionType === 'sso') {
      await this.sessionExtension();
    }
  }

  /**
   * Evaluates the session state and takes one action:
   * - Logs out if the 10-hour session cap has been reached.
   * - Silently renews the session when the user is active and within 15 minutes of the cap.
   * - Shows the expiry prompt when the user is inactive and within 4 minutes of the cap, or after the
   *   configured inactivity limit. Only one prompt is open at a time.
   */
  async sessionExtension() {
    // If valid: when close to expiry, ask user to extend or logout.
    const now = Date.now();
    const elapsed = now - this.sessionStartedAt;
    const remaining = this.sessionMaxMs - elapsed;
    const inactivityDuration = now - this.lastActivityAt;
    // Consider user active if they interacted within the active window.
    const userRecentlyActive = now - this.lastActivityAt <= this.activeWindowMs;

    if (remaining <= 0) {
      await this.handleLogoutSafely();
      return;
    }

    // Auto-renew in the last 15 minutes only for recently active users when no dialog is open.
    if (remaining <= this.autoRenewThresholdMs && userRecentlyActive && !this.promptInProgress) {
      try {
        await this.renewSession();
        return;
      } catch (error) {
        console.error('Auto renew failed', error);
      }
    }

    const shouldShowExpiryWarning = remaining <= this.warningThresholdMs && !userRecentlyActive;
    const shouldShowInactivityWarning = inactivityDuration >= this.inactivityWarningThresholdMs;

    // Show the warning when an inactive user is close to expiry, or after the configured inactivity limit.
    if (!this.promptInProgress && (shouldShowExpiryWarning || shouldShowInactivityWarning)) {
      this.promptInProgress = true;
      await new Promise<void>((resolve) => {
        this.zone.run(() => {
          void this.showSessionExpiryPrompt().finally(resolve);
        });
      });
    }
  }

  /**
   * Opens the session timeout dialog with a countdown equal to the warning threshold.
   * "Stay signed in" extends the session. "Sign out", a dismissal, or countdown expiry logs the user out.
   * The returned promise resolves once the dialog has closed.
   */
  private async showSessionExpiryPrompt(): Promise<void> {
    const timeoutMs = this.warningThresholdMs;
    const countdownEndAt = Date.now() + timeoutMs;

    this.clearWarningCountdownTimeout();
    this.warningCountdownTimeoutId = setTimeout(() => {
      void this.handleLogoutSafely();
    }, timeoutMs);

    this.sessionTimeoutDialogRef?.close();
    this.sessionTimeoutDialogRef = this.dialog.open(ConfirmationDialog, {
      panelClass: 'session-timeout-dialog-container',
      disableClose: true,
      data: {
        mode: 'session-timeout',
        heading: this.sessionTimeoutLabels.heading,
        description: this.sessionTimeoutLabels.description,
        supportingText: this.sessionTimeoutLabels.supportingText,
        supportingText2: this.sessionTimeoutLabels.supportingText2,
        button_one: this.sessionTimeoutLabels.signOut,
        button_two: this.sessionTimeoutLabels.staySignedIn,
        countdownEndAt
      }
    });

    this.sessionTimeoutDialogRef.afterClosed().subscribe((shouldExtend: boolean) => {
      this.sessionTimeoutDialogRef = null;
      if (shouldExtend) {
        void this.onExtendSessionSelected();
        return;
      }
      void this.handleLogoutSafely();
    });
  }

  /**
   * Handles "stay signed in". Closes the dialog, cancels the countdown, records the user's choice as activity,
   * and re-enables prompting for future checks.
   */
  private async onExtendSessionSelected(): Promise<void> {
    this.sessionTimeoutDialogRef?.close();
    this.sessionTimeoutDialogRef = null;
    this.clearWarningCountdownTimeout();

    const keepSignedInClickedAt = new Date().toISOString();
    console.log('[Session Timeout] Keep me signed in clicked at:', keepSignedInClickedAt);

    // Treat explicit user choice as activity and keep monitoring.
    this.lastActivityAt = Date.now();
    this.promptInProgress = false;
  }

  /**
   * Renews the backend session via the refresh endpoint, which sets the HttpOnly cookie.
   * Then asks the Gigya SDK to pick up the new cookies (errors are ignored) and resets the session and activity timers.
   * Rejects if the refresh request fails.
   */
  private async renewSession(): Promise<void> {
    // Call backend; let it set HttpOnly cookie via Set-Cookie
    const url = API_PATHS.AUTH.REFRESH_SESSION;
    const result = await this.baseService.get(url);

    console.log('Session renewed', result);

    // SPA:Gigya to re-process the refreshed session
    try {
      (window as any).gigya.socialize.refreshUI(); // ensures Web SDK picks up new cookies in Ajax SPA flows
    } catch { }
    // Reset our local "start time"
    this.sessionStartedAt = Date.now();
    this.lastActivityAt = Date.now();  // Also reset inactivity timer
  }

  /**
   * Ends the session and sends the user to the login route.
   * Closes the dialog and stops monitoring first. If the logout service fails, sets the FORCE_LOGIN cache flag
   * so the login page forces a fresh sign-in, then navigates anyway.
   */
  private async handleLogoutSafely(): Promise<void> {
    try {
      this.sessionTimeoutDialogRef?.close?.();
      this.sessionTimeoutDialogRef = null;
      this.stopMonitoring();
      await this.logoutService.logout();
      this.reusableService.navigateToUrl(`/${SRP_ROUTES.LOGIN.PATH}`, this.appContext, 'login');
    } catch (error) {
      console.log("Logout Error: ", error);
      this.cacheService.addToCache(CACHE_KEYS.LOGIN.FORCE_LOGIN, true);
      this.reusableService.navigateToUrl(`/${SRP_ROUTES.LOGIN.PATH}`, this.appContext, 'login');
    }
  }

  /**
   * Stops all monitoring. Cancels the countdown, closes any open dialog, clears the periodic check,
   * and removes the activity listeners. Safe to call when monitoring is not running.
   */
  stopMonitoring() {
    this.clearWarningCountdownTimeout();
    this.sessionTimeoutDialogRef?.close?.();
    this.sessionTimeoutDialogRef = null;

    clearInterval(this.intervalId);
    this.intervalId = undefined;

    this.activityEvents.forEach((ev) => window.removeEventListener(ev, this.onActivity));
  }

  /**
   * Cancels the pending auto-logout timer that runs when the expiry countdown reaches zero.
   */
  private clearWarningCountdownTimeout(): void {
    if (this.warningCountdownTimeoutId) {
      clearTimeout(this.warningCountdownTimeoutId);
      this.warningCountdownTimeoutId = undefined;
    }
  }
}
